"use client"

import { Check, ChevronDown, ChevronUp, Search } from "lucide-react"
import { useState } from "react"

import { PriceSlider } from "@/app/(root)/all-products/_components/price-slider"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { Filter } from "./filter-section"

interface FilterCategoryProps {
  filter: Filter
  expanded: boolean
  selectedValues: any
  onToggleExpand: () => void
  onChange: (values: string[] | { min: number; max: number }) => void
}

export function FilterCategory({
  filter,
  expanded,
  selectedValues,
  onToggleExpand,
  onChange,
}: FilterCategoryProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [price, setPrice] = useState([0, 200000])

  const handleCheckboxChange = (value: string, checked: boolean) => {
    const currentValues = Array.isArray(selectedValues) ? selectedValues : []
    if (checked) {
      onChange([...currentValues, value])
    } else {
      onChange(currentValues.filter((v) => v !== value))
    }
  }

  const handleRangeChange = (values: number[]) => {
    setPrice([values[0], values[1]])
    onChange({ min: values[0], max: values[1] })
  }

  // Add labels to options if they don't exist
  const optionsWithLabels = filter.options.map((option) => ({
    ...option,
    label: option.label || option.value, // Use value as label if label doesn't exist
  }))

  const filteredOptions = optionsWithLabels.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="w-full bg-white rounded-lg px-3 py-2">
      <Button
        variant="ghost"
        className="w-full hover:bg-white flex items-center justify-between p-0 h-auto"
        onClick={onToggleExpand}
      >
        <span className="font-gilroyMedium">{filter.title}</span>
        {expanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </Button>

      {expanded && (
        <div className="mt-2 space-y-2 font-gilroyMedium">
          {filter.title.includes("Brand") && (
            <div className="relative bg-[#F7F7F7] rounded-md">
              <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                placeholder={`Search ${filter.title.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-9 bg-transparent border-none placeholder:text-sm text-sm placeholder:text-[#D5D5D5] placeholder:font-gilroyMedium"
              />
            </div>
          )}

          {filter.type === "checkbox" && (
            <div className="space-y-2 max-h-[200px] overflow-y-auto pt-1">
              {filteredOptions.map((option) => {
                const isSelected =
                  Array.isArray(selectedValues) &&
                  selectedValues?.includes(option.value)
                const hasIcons = !!option.selected && !!option.unselected

                return (
                  <div
                    key={option.value}
                    className={cn(
                      "flex items-center justify-between space-x-2 p-2 rounded-md cursor-pointer  transition-colors",
                      isSelected
                        ? "bg-muted"
                        : "border-transparent hover:bg-muted"
                    )}
                    onClick={() =>
                      handleCheckboxChange(option.value, !isSelected)
                    }
                  >
                    <div className="flex gap-2 items-center">
                      {hasIcons && (
                        <div className="w-5 h-5 flex items-center justify-center">
                          {isSelected ? (
                            <img
                              src={option.selected || "/placeholder.svg"}
                              alt={`${option.label} selected`}
                              className="w-4 h-4"
                            />
                          ) : (
                            <img
                              src={option.unselected || "/placeholder.svg"}
                              alt={option.label}
                              className="w-4 h-4"
                            />
                          )}
                        </div>
                      )}
                      <span className="text-sm font-normal cursor-pointer">
                        {option.label}
                      </span>
                    </div>
                    {isSelected && <Check className="size-4" />}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}} // Handled by the div onClick
                      className="sr-only" // Hide the actual checkbox
                    />
                  </div>
                )
              })}
            </div>
          )}

          {filter.type === "range" && (
            <PriceSlider
              values={price}
              setValues={(e) => {
                setPrice(e)
                handleRangeChange(e as number[])
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
